import React, { useEffect, useState, useMemo } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useScreenType } from '../../hooks/useScreenType';
import './MyAssetsScreen.css';

import DesktopMyAssets, { Asset, CalculatedMetrics } from './DesktopMyAssets';
import MobileMyAssets from './MobileMyAssets';

interface PerformanceMetrics {
  investissement_initial: number;
  valeur_actuelle: number;
  plus_minus_value_total: number;
  performance_total: number;
  performance_hebdo: number;
  performance_mensuel: number;
  performance_annuel: number;
  day_pl: number;
  day_pl_pct: number;
}

const MyAssetsScreen: React.FC = () => {
  const { token } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<number>(0);
  const [portfolioHistory, setPortfolioHistory] = useState<{ time: string; value: number }[]>([]);

  useEffect(() => {
    if (token) {
      fetchPortfolioData();
      fetchBalance();
    }
  }, [token]);

  useEffect(() => {
    if (assets.length > 0) {
      calculateHistory();
    }
  }, [assets]);

  const calculateHistory = () => {
    const maxLength = Math.max(
      ...assets.map((a) => (a.sparkline_data ? a.sparkline_data.length : 0))
    );
    if (maxLength === 0) return;

    const history: { time: string; value: number }[] = [];
    const today = new Date();

    for (let i = 0; i < maxLength; i++) {
      let dailyTotal = 0;
      assets.forEach((asset) => {
        if (asset.sparkline_data && asset.sparkline_data.length > 0) {
          const dataIndex = asset.sparkline_data.length - 1 - i;
          if (dataIndex >= 0) {
            dailyTotal += asset.sparkline_data[dataIndex] * asset.quantite;
          } else {
            dailyTotal += asset.sparkline_data[0] * asset.quantite;
          }
        }
      });
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      history.unshift({
        time: date.toISOString().split('T')[0],
        value: dailyTotal,
      });
    }
    setPortfolioHistory(history);
  };

  const fetchPortfolioData = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/portfolio/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setAssets(data.assets || []);
      setMetrics(data.metrics || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/account/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const calculatedMetrics = useMemo(() => {
    if (!metrics) return null;

    return {
      // Valeur des positions uniquement (actions) — même périmètre que
      // "Valeur Totale" sur Analyse Portefeuille (/v1/portfolio/analysis
      // total_value, hors solde espèces). totalValue reste le grand total
      // (positions + espèces) pour ne perdre aucune information.
      portfolioValue: metrics.valeur_actuelle,
      totalValue: metrics.valeur_actuelle + balance,
      totalPL: metrics.plus_minus_value_total,
      totalPLPct: metrics.performance_total,
      dayPL: metrics.day_pl || 0,
      dayPLPct: metrics.day_pl_pct || 0,
      cashBalance: balance,
      investedAmount: metrics.investissement_initial,
    };
  }, [metrics, balance]);

  const { isMobile } = useScreenType();

  if (loading) {
    return (
      <SiteLayout>
        <Header icon='account_balance_wallet' title='Mes Actifs' />
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <div className='myassets-spinner' />
          <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Chargement de vos actifs...</div>
        </div>
      </SiteLayout>
    );
  }

  const childProps = {
    assets,
    calculatedMetrics,
    portfolioHistory,
  };

  return (
    <SiteLayout>
      <Header icon='account_balance_wallet' title='Mes Actifs' />
      {isMobile ? (
        <MobileMyAssets {...childProps} />
      ) : (
        <DesktopMyAssets {...childProps} />
      )}
    </SiteLayout>
  );
};

export default MyAssetsScreen;
